<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanItem from './KanbanItem.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: 'orange',
  },
  items: {
    type: Array,
    required: true,
  },
  count: {
    type: Number,
    default: 0,
  },
  totalColumns: {
    type: Number,
    default: 0,
  },
  agentsList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add', 'edit', 'delete', 'drop', 'itemClick']);
const { t } = useI18n();

const columnTotal = computed(() => {
  return props.items.reduce((total, item) => {
    const value = parseFloat(item.item_details?.value) || 0;
    return total + value;
  }, 0);
});

const formattedTotal = computed(() => {
  if (!columnTotal.value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(columnTotal.value);
});

const darkenColor = color => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenAmount = 0.85;
  const dr = Math.floor(r * darkenAmount);
  const dg = Math.floor(g * darkenAmount);
  const db = Math.floor(b * darkenAmount);

  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
};

const textColor = computed(() =>
  props.color ? darkenColor(props.color) : '#CC6E00'
);

const columnStyle = computed(() => ({
  backgroundColor: props.color ? `${props.color}40` : '#f8fafc',
}));

const countStyle = computed(() => ({
  backgroundColor: props.color ? `${props.color}15` : '#FFF4E5',
  color: textColor.value,
}));

const handleDrop = event => {
  const itemId = event.dataTransfer.getData('text/plain');
  emit('drop', { itemId, columnId: props.id });
};

const handleDragOver = event => {
  event.preventDefault();
};

const columnWidth = computed(() => {
  if (props.totalColumns <= 5) {
    return {
      minWidth: '320px',
      maxWidth: `calc((100% - ${(props.totalColumns - 1) * 1}rem) / ${props.totalColumns})`,
      width: '100%',
    };
  }
  return {
    minWidth: '350px',
    maxWidth: '350px',
    width: '350px',
  };
});
</script>

<template>
  <div
    class="flex flex-col rounded-lg overflow-hidden"
    :style="{ ...columnWidth, ...columnStyle }"
  >
    <div
      class="pl-4 pr-4 pt-2 pb-2"
      :style="{ borderBottom: `1px solid ${props.color}59` }"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium" :style="{ color: textColor }">
            {{ title }}
          </h3>
          <span
            class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full"
            :style="countStyle"
          >
            {{ count }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="formattedTotal"
            class="text-xs font-medium"
            :style="{ color: textColor }"
          >
            {{ formattedTotal }}
          </span>
          <button
            class="p-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            @click="$emit('add', id)"
          >
            <fluent-icon icon="add" size="16" />
          </button>
        </div>
      </div>
    </div>

    <div
      class="flex-1 overflow-y-auto"
      @drop="handleDrop"
      @dragover="handleDragOver"
    >
      <div class="px-4">
        <div class="py-3 space-y-3">
          <template v-if="items.length">
            <KanbanItem
              v-for="item in items"
              :key="item.id"
              :item="item"
              :agents-list="agentsList"
              draggable
              @dragstart="
                e => {
                  e.dataTransfer.setData('text/plain', item.id.toString());
                }
              "
              @click="$emit('itemClick', item)"
              @edit="$emit('edit', item)"
              @delete="$emit('delete', item)"
            />
          </template>
          <div
            v-else
            class="flex items-center justify-center h-24 text-sm text-slate-500 dark:text-slate-400"
          >
            {{ t('KANBAN.NO_ITEMS') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.overflow-y-auto {
  min-height: 0;
  height: 100%;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.space-y-3 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.75rem;
}

.space-y-3 > :last-child {
  margin-bottom: 0;
}
</style>
